import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Text from '../shared/Text'
import styles from '../../utility/styles'
import { returnTimeAgo, returnInitials } from '../../utility/helpers'

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	background-color: var(--tz-surface-raised, #1A202C);
	margin-bottom: 12px;
	border-radius: 8px;
	overflow: hidden;
`

const Header = styled.div`
	display: flex;
	justify-content: flex-end;
	flex-direction: column;
	position: relative;
	background-color: var(--tz-surface-raised, #1A202C);
	background-image: url('${({ backgroundImg }) => backgroundImg}');
	background-repeat: no-repeat;
	background-size: cover;
	margin-bottom: ${({ backgroundImg }) => (backgroundImg ? '8px' : '0')};
`

const Mask = styled.div`
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	height: 80px;
	background: linear-gradient(
		180deg,
		rgba(0, 0, 0, 0) 0%,
		rgba(0, 0, 0, 0.72) 100%
	);
`

const Row = styled.div`
	display: flex;
	align-items: center;
`

const Col = styled.div`
	display: flex;
	flex-direction: column;
`

const HeaderRow = styled.div`
	display: flex;
	justify-content: space-between;
	margin: 16px 16px 12px 16px;
	z-index: 100;
`

const Footer = styled.div`
	margin: 0 16px 16px 16px;
`

const Divider = styled.div`
	height: 1px;
	background-color: ${styles.colors.background};
	margin-bottom: 8px;
`

const Span = styled.span`
	font-weight: bold;
`

const Avatar = styled.div`
	height: 32px;
	width: 32px;
	border-radius: 16px;
	margin: 0 8px 0 0;
	background: center / contain ${styles.colors.background}
		url(${({ src }) => src});
	display: flex;
	justify-content: center;
	align-items: center;
	border: ${({ src }) =>
		!!src ? 'none' : `2px solid ${styles.colors.green}`};
`

const AvatarSmall = styled.img`
	height: 16px;
	width: 16px;
	border-radius: 8px;
	margin: 0 8px 0 0;
`

const returnRatio = src => {
	return new Promise(res => {
		const image = new Image()
		image.src = src
		image.addEventListener('load', ({ target }) => {
			res(target?.naturalHeight / target?.naturalWidth)
		})
	})
}

const returnHeight = async (src, id) => {
	const width = document.getElementById(id)?.clientWidth
	const ratio = await returnRatio(src)
	return width * ratio
}

/**
 *
 * @param {{
 *   user:{image:string,name:string,model_name:string},
 *   body:string,
 *   reply_glimpse:{body:string,user_name:string},
 *   replies_count:number,
 *   likes:number,
 *   like_avatars:string[],
 *   media:string
 * }} props
 */
export default function Post({
	user,
	body,
	reply_glimpse,
	replies_count,
	likes,
	like_avatars = [],
	media,
	created_at,
}) {
	const [height, setHeight] = useState('auto')
	const backgroundImg = Array.isArray(media) ? media[0]?.url : ''

	useEffect(() => {
		const getIt = async () => {
			const h = await returnHeight(media[0]?.url, `post-header-${created_at}`)
			if (!Number.isNaN(h)) setHeight(`${h}px`)
		}
		getIt()
	}, [media, created_at])

	return (
		<Wrapper>
			<Header
				{...{ backgroundImg }}
				id={`post-header-${created_at}`}
				style={{ height }}
			>
				{!!backgroundImg && <Mask />}
				<HeaderRow>
					<Row>
						<Avatar src={user.image}>
							{!user.image && (
								<Text.Tiny
									variant='bold'
									color={styles.colors.green}
								>
									{returnInitials(user.name)}
								</Text.Tiny>
							)}
						</Avatar>
						<Row>
							<Col>
								<Text.Body variant='bold'>
									{user.name}
								</Text.Body>
								<Text.Tiny
									color={styles.colors.lightGray}
									variant='bold'
								>
									{user.model_name}
								</Text.Tiny>
							</Col>
						</Row>
					</Row>
					<Row>
						<svg
							width='16'
							height='16'
							viewBox='0 0 16 16'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'
						>
							<path
								d='M7.9987 1.33337C4.32251 1.33337 1.33203 4.32385 1.33203 8.00004C1.33203 11.6762 4.32251 14.6667 7.9987 14.6667C11.6749 14.6667 14.6654 11.6762 14.6654 8.00004C14.6654 4.32385 11.6749 1.33337 7.9987 1.33337ZM7.52251 2.4048V4.34925H6.07409C6.14354 4.16758 6.21422 3.98901 6.29235 3.82842C6.65879 3.07631 7.09344 2.60756 7.52251 2.4048ZM8.47489 2.4048C8.90395 2.60755 9.3386 3.07631 9.70505 3.82842C9.78317 3.98901 9.85386 4.16758 9.9233 4.34925H8.47489V2.4048ZM5.846 2.70737C5.69905 2.92501 5.56202 3.16001 5.43925 3.41174C5.29789 3.70129 5.17387 4.01628 5.06227 4.34923H3.60394C4.20041 3.63123 4.96678 3.06388 5.846 2.70733V2.70737ZM10.1516 2.70737C11.0308 3.0639 11.7971 3.63123 12.3936 4.34928H10.9353C10.8237 4.01631 10.6997 3.70134 10.5583 3.41179C10.4355 3.16006 10.2985 2.92506 10.1516 2.70742V2.70737ZM2.95917 5.30166H4.79441C4.6394 5.9899 4.54454 6.73891 4.51663 7.52388H2.30425C2.36998 6.72402 2.5994 5.97317 2.95902 5.30166H2.95917ZM5.77663 5.30166H7.52267V7.52388H5.46902C5.50064 6.72464 5.60976 5.97317 5.77656 5.30166H5.77663ZM8.47505 5.30166H10.2211C10.3879 5.97317 10.497 6.72464 10.5286 7.52388H8.47497L8.47505 5.30166ZM11.2033 5.30166H13.0385C13.3982 5.97317 13.6276 6.72402 13.6933 7.52388H11.4809C11.453 6.73891 11.3582 5.9899 11.2031 5.30166H11.2033ZM2.30457 8.47626H4.51695C4.54485 9.26309 4.6391 10.0102 4.79473 10.6985H2.95949C2.60049 10.027 2.37046 9.27487 2.30473 8.47626H2.30457ZM5.46933 8.47626H7.52298V10.6985H5.77695C5.61078 10.0282 5.50041 9.27425 5.46941 8.47626H5.46933ZM8.47536 8.47626H10.529C10.498 9.27425 10.3876 10.0282 10.2215 10.6985H8.47544L8.47536 8.47626ZM11.4814 8.47626H13.6938C13.6281 9.27487 13.398 10.027 13.039 10.6985H11.2038C11.3594 10.0102 11.4537 9.26309 11.4816 8.47626H11.4814ZM3.59949 11.6509H5.06279C5.1744 11.9832 5.29841 12.2938 5.43978 12.5834C5.56379 12.8382 5.70267 13.0776 5.85149 13.2977C4.96855 12.9405 4.19784 12.3726 3.59943 11.6509H3.59949ZM6.07473 11.6509H7.52314V13.6002C7.09408 13.3962 6.65943 12.9188 6.29298 12.1667C6.21486 12.0055 6.14417 11.8325 6.07473 11.6508V11.6509ZM8.47552 11.6509H9.92394C9.85449 11.8325 9.78381 12.0055 9.70568 12.1667C9.33924 12.9188 8.90459 13.3963 8.47552 13.6003V11.6509ZM10.9358 11.6509H12.3991C11.8008 12.3726 11.0301 12.9405 10.1471 13.2977C10.2959 13.0776 10.4348 12.8382 10.5588 12.5834C10.7002 12.2938 10.8242 11.9832 10.9358 11.6509H10.9358Z'
								fill='#657185'
							/>
						</svg>
						<Text.Tiny
							color={styles.colors.lightGray}
							style={{ marginLeft: '4px' }}
						>
							{returnTimeAgo(created_at)}
						</Text.Tiny>
					</Row>
				</HeaderRow>
			</Header>
			<Text.Body style={{ margin: '0 16px 12px 16px' }}>{body}</Text.Body>
			{(Boolean(likes) || Boolean(reply_glimpse)) && (
				<Footer>
					<Divider />
					{Boolean(likes) && (
						<Row>
							<Text.Tiny
								variant='bold'
								style={{ marginRight: '8px' }}
							>
								{likes} Like{likes > 1 ? 's' : ''}
							</Text.Tiny>
							{like_avatars.map(avatar => (
								<AvatarSmall src={avatar} />
							))}
						</Row>
					)}
					{Boolean(reply_glimpse) && (
						<Text.Caption style={{ marginTop: '8px' }}>
							<Span>{reply_glimpse.user_name} </Span>
							{reply_glimpse.body}
						</Text.Caption>
					)}
					{Boolean(replies_count) && (
						<Text.Tiny
							color={styles.colors.lightGray}
							variant='bold'
							style={{ marginTop: '8px' }}
						>
							{replies_count} comment
							{replies_count > 1 ? 's' : ''}
						</Text.Tiny>
					)}
				</Footer>
			)}
		</Wrapper>
	)
}
